import React, { useEffect } from "react";
import { Typography, Box, CircularProgress, TextField, Stack } from "@mui/material";
import { WorkingHoursSelector } from "components/ClinicianWorkingHours";
import { useQuery } from "react-query";
import { getClinicianWorkingHours } from "api/clinician";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";
import { CLINICIAN_WORKING_HOURS } from "@constants/reactQueryKeys";
import { SectionBox } from "@answersnow/pkg-theme/components";
import { getUserPermissionsList } from "selectors";
import { useSelector } from "react-redux";
import { UserPermissions } from "types/permissions";

const daysOrder = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

interface WorkingHourRange {
  start: string;
  end: string;
}

interface DayWorkingHours {
  isActive: boolean;
  ranges: WorkingHourRange[];
}

interface WorkingHours {
  [day: string]: DayWorkingHours;
}

interface ClinicianBasicInfo {
  user_id: string | number;
  timezone: string;
}

interface ClinicianWorkingHoursProps {
  editMode: boolean;
  basicInfo: ClinicianBasicInfo;
  newWorkingHours: WorkingHours;
  setNewWorkingHours: (workingHours: WorkingHours) => void;
  onChangeWorkingHours: () => void;
  maxBillableHour: number | "";
  setMaxBillableHour: (hour: number | "") => void;
}

export const ClinicianWorkingHours = ({
  editMode,
  basicInfo,
  newWorkingHours,
  setNewWorkingHours,
  onChangeWorkingHours,
  maxBillableHour,
  setMaxBillableHour,
}: ClinicianWorkingHoursProps) => {
  const userPermissions = useSelector(getUserPermissionsList) as UserPermissions | null;
  const { data: body, isLoading } = useQuery<{ data: WorkingHours }>(
    [CLINICIAN_WORKING_HOURS, basicInfo.user_id],
    () => getClinicianWorkingHours(String(basicInfo.user_id)),
    {
      onError: (error) => {
        console.error("Error retrieving clinician working hours:", error);
      },
    }
  );

  useEffect(() => {
    if (body?.data && (!editMode || Object.keys(newWorkingHours).length === 0)) {
      setNewWorkingHours(body.data);
    }
  }, [body, editMode, newWorkingHours, setNewWorkingHours]);

  const updateDayData = (day: string, isActive: boolean, newRanges: WorkingHourRange[]) => {
    setNewWorkingHours({
      ...newWorkingHours,
      [day]: { isActive, ranges: newRanges },
    });
    onChangeWorkingHours();
  };

  const copyToDays = (chosenDay: string, selectedDays: string[]) => {
    setNewWorkingHours({
      ...selectedDays.reduce(
        (acc, day) => {
          acc[day] = newWorkingHours[chosenDay];
          return acc;
        },
        { ...newWorkingHours }
      ),
    });
    onChangeWorkingHours();
  };

  return (
    <Box display="flex" justifyContent="center">
      <Box width="80%" maxWidth="1200px" padding={"16px 0px"}>
        <SectionBox
          title="Working Hours"
          description={
            !userPermissions?.edit_clinician_working_hours &&
            userPermissions?.view_clinician_working_hours
              ? `Contact your clinical supervisor to change your working hours.`
              : `To set the Clinician’s working hours for therapy sessions, please indicate the days
                  and times they are available.`
          }
          columnDirection
        >
          <Box display="flex" flexDirection="column" width="fit-content">
            <Typography variant="inputLabel" color="text.secondary" mb="8px">
              Working Hours
            </Typography>
            {!!newWorkingHours && !isLoading ? (
              <>
                <Stack gap={editMode ? "16px" : "8px"}>
                  {daysOrder.map((day) => {
                    const dayData = newWorkingHours[day];
                    if (dayData) {
                      return (
                        <WorkingHoursSelector
                          key={day}
                          day={day}
                          isActive={dayData.isActive}
                          clinicianTimeZone={basicInfo.timezone}
                          timeRanges={dayData.ranges}
                          updateDayData={updateDayData}
                          editMode={editMode}
                          copyToDays={copyToDays}
                        />
                      );
                    }
                    return null; // Return null if there's no data for the day
                  })}
                </Stack>
                {editMode ? (
                  <>
                    <Typography
                      variant="body.caption"
                      sx={{ fontStyle: "italic", margin: "16px 0 24px" }}
                    >{`All times are in the clinician's time zone (${
                      formatTimeZoneToAbbreviation(basicInfo.timezone) || "UTC"
                    })`}</Typography>
                    <TextField
                      name="maxBillableHour"
                      label="Max billable hours"
                      value={maxBillableHour}
                      onChange={(e) =>
                        setMaxBillableHour(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      type="number"
                      fullWidth
                      inputProps={{ step: 0.5 }}
                    />
                  </>
                ) : (
                  <>
                    <Typography margin="24px 0 8px" variant="inputLabel" color="text.secondary">
                      Max Billable Hours
                    </Typography>
                    <Typography variant="body.small">{maxBillableHour || "-"}</Typography>
                  </>
                )}
              </>
            ) : (
              <CircularProgress size={30} color="primary" style={{ marginBottom: 10 }} />
            )}
          </Box>
        </SectionBox>
      </Box>
    </Box>
  );
};

export default ClinicianWorkingHours;
